// Tiny presentational helpers shared across the role dashboards.
// Adapted from credentik-app/v2 + MemberMD V2 primitives.tsx.

import type { ReactNode, ButtonHTMLAttributes } from 'react';
import type { LucideIcon } from 'lucide-react';
import { cn } from '../../lib/cn';

// ── Card ────────────────────────────────────────────────────────────────

export function Card({
  children, className, padding = 'p-5',
}: { children: ReactNode; className?: string; padding?: string }) {
  // Voye card language: 18px radius, hairline border, soft shadow (not a flat
  // outline). Matches the mobile app's surfaces so both read as one product.
  return (
    <div
      className={cn('bg-white rounded-[var(--radius-card)]', padding, className)}
      style={{ border: '1px solid var(--color-border)', boxShadow: 'var(--shadow-thumb)' }}
    >
      {children}
    </div>
  );
}

// ── Button (Voye spec: 16px radius, interactive blue, weights) ────────
// One CTA shape across the app so web matches the mobile app. Variants:
// primary (filled interactive blue), secondary (tinted), ghost (bordered).

type BtnVariant = 'primary' | 'secondary' | 'ghost' | 'danger';
type BtnSize = 'md' | 'sm';

const BTN_VARIANTS: Record<BtnVariant, string> = {
  primary:   'text-white hover:brightness-[1.06] disabled:opacity-50',
  secondary: 'text-[var(--color-primary)] hover:brightness-[0.97] disabled:opacity-50',
  ghost:     'text-[var(--color-body)] hover:bg-[var(--color-tint-3)] disabled:opacity-50',
  danger:    'text-white hover:brightness-[1.06] disabled:opacity-50',
};

const BTN_BG: Record<BtnVariant, string> = {
  primary:   'var(--color-interactive)',
  secondary: 'var(--color-tint)',
  ghost:     'transparent',
  danger:    'var(--color-danger-fg)',
};

export function Button({
  children, variant = 'primary', size = 'md', className, fullWidth, type = 'button', ...rest
}: {
  children: ReactNode;
  variant?: BtnVariant;
  size?: BtnSize;
  className?: string;
  fullWidth?: boolean;
} & ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button
      type={type}
      className={cn(
        'inline-flex items-center justify-center gap-1.5 font-bold transition rounded-[var(--radius-button)]',
        size === 'md' ? 'px-4 py-2.5 text-[13.5px]' : 'px-3 py-1.5 text-[12px]',
        fullWidth && 'w-full',
        BTN_VARIANTS[variant],
        className,
      )}
      style={{
        background: BTN_BG[variant],
        border: variant === 'ghost' ? '1px solid var(--color-border)' : undefined,
      }}
      {...rest}
    >
      {children}
    </button>
  );
}

// ── Section header (smaller than PortalShell's TopBar h1) ────────────

export function SectionHeader({
  title, subtitle, actions,
}: { title: string; subtitle?: string; actions?: ReactNode }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-2 mb-3">
      <div>
        <h2 className="text-[15px] font-semibold tracking-tight text-slate-900">{title}</h2>
        {subtitle && <p className="text-[12px] text-slate-500 mt-0.5">{subtitle}</p>}
      </div>
      {actions && <div className="flex items-center gap-2">{actions}</div>}
    </div>
  );
}

// ── Stat tile (one of 4 in a typical dashboard hero row) ──────────────

interface StatTileProps {
  icon: LucideIcon;
  label: string;
  value: string;
  hint?: string;
  trend?: { value: string; positive: boolean };
  tone?: 'default' | 'brand' | 'emerald' | 'amber' | 'rose';
}

const TILE_TONES: Record<NonNullable<StatTileProps['tone']>, string> = {
  default: 'bg-white text-slate-600',
  brand:   'bg-brand-50 text-brand-700',
  emerald: 'bg-emerald-50 text-emerald-700',
  amber:   'bg-amber-50 text-amber-700',
  rose:    'bg-rose-50 text-rose-700',
};

export function StatTile({ icon: Icon, label, value, hint, trend, tone = 'default' }: StatTileProps) {
  return (
    <Card padding="p-4">
      <div className="flex items-start justify-between">
        <span className={cn('inline-flex w-9 h-9 items-center justify-center rounded-lg', TILE_TONES[tone])}>
          <Icon className="w-4 h-4" />
        </span>
        {trend && (
          <span className={cn('text-[11px] font-semibold inline-flex items-center gap-0.5',
            trend.positive ? 'text-emerald-700' : 'text-rose-700')}>
            {trend.positive ? '▲' : '▼'} {trend.value}
          </span>
        )}
      </div>
      <p className="mt-3 text-[24px] font-extrabold tracking-tight text-[var(--color-ink)] tabular-nums">{value}</p>
      <p className="text-[12px] font-semibold text-[var(--color-secondary)]">{label}</p>
      {hint && <p className="mt-1 text-[11px] text-slate-400">{hint}</p>}
    </Card>
  );
}

// ── Activity row (recent-events feed) ─────────────────────────────────

interface ActivityRowProps {
  icon: LucideIcon;
  iconTone?: 'brand' | 'emerald' | 'amber' | 'rose' | 'slate';
  title: ReactNode;
  meta?: ReactNode;
  time?: string;
}

const ACT_ICON_TONES: Record<NonNullable<ActivityRowProps['iconTone']>, string> = {
  brand:   'bg-brand-50 text-brand-600',
  emerald: 'bg-emerald-50 text-emerald-600',
  amber:   'bg-amber-50 text-amber-600',
  rose:    'bg-rose-50 text-rose-600',
  slate:   'bg-slate-100 text-slate-600',
};

export function ActivityRow({ icon: Icon, iconTone = 'slate', title, meta, time }: ActivityRowProps) {
  return (
    <div className="flex items-start gap-3 py-2.5">
      <span className={cn('inline-flex w-8 h-8 items-center justify-center rounded-full shrink-0', ACT_ICON_TONES[iconTone])}>
        <Icon className="w-3.5 h-3.5" />
      </span>
      <div className="flex-1 min-w-0">
        <p className="text-[13px] text-slate-900 leading-snug">{title}</p>
        {meta && <p className="text-[11.5px] text-slate-500 mt-0.5">{meta}</p>}
      </div>
      {time && <span className="text-[11px] text-slate-400 shrink-0 mt-1">{time}</span>}
    </div>
  );
}

// ── Empty state (when a section's data list is empty) ─────────────────

export function EmptyState({
  icon: Icon, title, body, action,
}: { icon: LucideIcon; title: string; body?: string; action?: ReactNode }) {
  return (
    <div className="text-center py-10 px-4">
      <span className="inline-flex w-12 h-12 items-center justify-center rounded-full bg-slate-100 text-slate-400">
        <Icon className="w-5 h-5" />
      </span>
      <p className="mt-3 text-[14px] font-semibold text-slate-900">{title}</p>
      {body && <p className="mt-1 text-[12.5px] text-slate-500 max-w-sm mx-auto">{body}</p>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}

// ── Demo data callout (label placeholder UI honestly) ─────────────────

export function DemoDataBadge({ note }: { note?: string }) {
  return (
    <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md bg-amber-50 text-amber-800 border border-amber-200 text-[10px] font-semibold uppercase tracking-wider">
      Demo data{note && <span className="font-normal normal-case text-amber-700"> · {note}</span>}
    </span>
  );
}

// ── Status pill (reusable across role pages) ───────────────────────────

const STATUS_TONES: Record<string, string> = {
  active:       'bg-emerald-50 text-emerald-700',
  approved:     'bg-emerald-50 text-emerald-700',
  succeeded:    'bg-emerald-50 text-emerald-700',
  confirmed:    'bg-emerald-50 text-emerald-700',
  paid:         'bg-emerald-50 text-emerald-700',
  delivered:    'bg-emerald-50 text-emerald-700',
  pending:      'bg-amber-50 text-amber-700',
  applied:      'bg-amber-50 text-amber-700',
  kyb_review:   'bg-amber-50 text-amber-700',
  submitted:    'bg-sky-50 text-sky-700',
  quote_pending:'bg-sky-50 text-sky-700',
  quoted:       'bg-sky-50 text-sky-700',
  in_progress:  'bg-violet-50 text-violet-700',
  preparing:    'bg-violet-50 text-violet-700',
  ready:        'bg-violet-50 text-violet-700',
  failed:       'bg-rose-50 text-rose-700',
  disputed:     'bg-rose-50 text-rose-700',
  cancelled:    'bg-slate-100 text-slate-600',
  draft:        'bg-slate-100 text-slate-600',
};

export function StatusPill({ status }: { status: string }) {
  const norm = String(status).toLowerCase().replace(/\s+/g, '_');
  const tone = STATUS_TONES[norm] ?? STATUS_TONES.draft;
  return (
    <span className={cn('inline-flex items-center px-2 py-0.5 rounded text-[11px] font-medium capitalize', tone)}>
      {norm.replace(/_/g, ' ')}
    </span>
  );
}
